import * as React from "react";
import { InputCommonProps, ValidatedInputProps } from "./Common";
import commonStyles from "./Common.module.scss";
import { classNames } from "../../Utilities";
import { useForceUpdate } from "../../Hooks";
import { Fragment } from "react";
import styles from "./RadioCheckButtons.module.scss";
import { Button, ButtonSize } from "../Button";
import { Severity } from "../../Helpers";

interface RadioCheckButtonsProps<T> {
    multiple?: boolean;
    items: T[];
    descriptions: (item: T) => string;
    values: (item: T) => string;
    categories?: (item: T) => string | null;
    value?: string[];
    exclusiveValues?: string[];
    separateExclusiveValues?: Separation;
    onChange?: (value: string[]) => void;
    uniform?: boolean;
    fullWidth?: boolean;
    size?: ButtonSize;
}

export enum Separation {
    None,
    Before,
    After
}

export const RadioCheckButtons = <T,>(props: RadioCheckButtonsProps<T> & ValidatedInputProps & InputCommonProps) => {
    const {
        validator,
        validations,
        customMessages,
        label
    } = props;

    const forceUpdate = useForceUpdate();

    const handleItemClicked = (item: T) => {
        const selectedValue = props.values(item);

        if (!(props.value ?? []).includes(selectedValue)) {
            if (!props.multiple
                || (props.exclusiveValues ?? []).includes(selectedValue)
                || (props.value?.length === 1 && (props.exclusiveValues ?? []).includes(props.value[0]))
            ) {
                props.onChange?.([selectedValue]);
            }
            else {
                props.onChange?.([...props.value ?? [], selectedValue]);
            }
        }
        else {
            props.onChange?.(props.value?.filter(v => v !== selectedValue) ?? []);
        }
    };

    const [items, gapBefore] = React.useMemo(() => {
        if (props.separateExclusiveValues) {
            const exclusive = props.items.filter(a => props.exclusiveValues?.includes(props.values(a)));
            const nonExclusive = props.items.filter(a => !props.exclusiveValues?.includes(props.values(a)));
            if (props.separateExclusiveValues === Separation.After) {
                return [[...nonExclusive, ...exclusive], nonExclusive.length];
            }
            else {
                return [[...exclusive, ...nonExclusive], exclusive.length];
            }
        }
        else {
            return [props.items, 0];
        }
    }, [props.separateExclusiveValues]);

    React.useEffect(() => {
        validator?.register({
            key: props.id,
            validations: validations,
            value: props.value,
            forceUpdate: forceUpdate,
            validationMessages: customMessages
        });

        return () => {
            validator?.unregister(props.id);
        };
    }, []);

    React.useEffect(() => {
        if (validator) {
            validator.setValue(props.id, props.value ?? null);
            forceUpdate();
        }
    }, [props.value]);

    const isErrorState = (): boolean => {
        return !!props.validator && props.validator.submitAttempted() && props.validator.getErrors(props.id).length > 0;
    };

    let currentCategory: string | null = null;

    const renderCategory = (item: T) => {
        if (!props.categories) return;

        currentCategory = props.categories(item);
        return <div className={styles.category}>{props.categories(item)}</div>;
    };

    return (
        <div className={commonStyles.container}>
            {!!props.label && (
                <div className={classNames([commonStyles.label, isErrorState() && commonStyles.error])}>
                    <label htmlFor={props.id}>{label}</label>
                </div>
            )}

            <div
                className={classNames([
                    styles.items
                ])}
            >
                {items.map((item, index) => (
                    <Fragment key={props.values(item)}>
                        {!!props.separateExclusiveValues && gapBefore === index && (
                            <div className={styles.lineBreak}></div>
                        )}
                        {!!props.categories && props.categories(item) !== currentCategory && renderCategory(item)}
                        <Button
                            id={`${props.id}-${index}`}
                            className={classNames([
                                props.fullWidth && styles.fullWidth,
                                props.uniform && styles.uniform
                            ])}
                            severity={(props.exclusiveValues ?? []).includes(props.values(item)) ? Severity.Tertiary : undefined}
                            empty={!(props.value ?? []).includes(props.values(item))}
                            onClick={() => handleItemClicked(item)}
                            size={props.size}
                        >
                            {props.descriptions(item)}
                        </Button>
                    </Fragment>
                ))}
            </div>

            {isErrorState() && (
                <div className={commonStyles.validation}>
                    {props.validator?.getErrors(props.id).map((message) => {
                        return <div key={message}>{message}</div>;
                    })}
                </div>
            )}

        </div>
    );
};